import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { Home, Folder, Activity, LogOut, Menu, X, Search } from 'lucide-react';
import CommandBar from '../components/CommandBar';
import ClientHome from '../components/ClientHome';
import ClientProjects from '../components/ClientProjects';
import ActivityFeed from '../components/ActivityFeed';
import { Button } from '../components/ui/button';

const ClientDashboard = ({ user, onLogout }) => {
  const [showCommandBar, setShowCommandBar] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandBar(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/client' },
    { icon: Folder, label: 'My Projects', path: '/client/projects' },
    { icon: Activity, label: 'Activity', path: '/client/activity' }
  ];

  return (
    <div className="min-h-screen bg-surface-page flex" data-testid="client-dashboard">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } transition-all duration-300 bg-white/50 backdrop-blur-md border-r border-border-subtle flex-shrink-0 overflow-hidden`}
        data-testid="client-sidebar"
      >
        <div className="p-6">
          <h1 className="text-2xl font-bold text-brand mb-1">Ventas OS</h1>
          <p className="text-xs text-text-secondary">Client Portal</p>
        </div>

        <nav className="px-3 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-text-secondary hover:bg-brand-hover/10 hover:text-brand transition-colors"
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={20} />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-0 right-0 px-3">
          <div className="glass rounded-xl p-4 mb-3">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-moonstone to-billiard flex items-center justify-center text-white font-semibold">
                {user.name.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-semibold text-text-primary">{user.name}</p>
                <p className="text-xs text-text-secondary">{user.role}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="w-full justify-start text-negative hover:text-negative hover:bg-negative/10"
              data-testid="client-logout-button"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>

          <button
            onClick={() => setShowCommandBar(true)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-moonstone/5 border border-brand/20 text-text-secondary hover:bg-brand/10 transition-colors"
            data-testid="client-cmd-bar-trigger"
          >
            <div className="flex items-center gap-2">
              <Search size={16} />
              <span className="text-sm">Search...</span>
            </div>
            <kbd className="px-2 py-1 text-xs font-semibold text-brand bg-white rounded">⌘K</kbd>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6 md:p-8 lg:p-12">
          {/* Mobile Header */}
          <div className="md:hidden flex items-center justify-between mb-6">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-brand/10"
              data-testid="client-mobile-menu-toggle"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <h1 className="text-xl font-bold text-brand">Ventas OS</h1>
            <div className="w-8" />
          </div>

          <Routes>
            <Route path="/" element={<ClientHome user={user} />} />
            <Route path="/projects" element={<ClientProjects user={user} />} />
            <Route path="/activity" element={<ActivityFeed user={user} />} />
          </Routes>
        </div>
      </main>

      {/* Command Bar */}
      {showCommandBar && (
        <CommandBar
          user={user}
          onClose={() => setShowCommandBar(false)}
          onNavigate={(path) => {
            navigate(path);
            setShowCommandBar(false);
          }}
        />
      )}
    </div>
  );
};

export default ClientDashboard;
